import { useToast } from '@/components/ui/use-toast';
import type { LoginPageQuery } from '@edwin-group/people-graphql';
import { login } from '@/services/auth/auth.service';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as z from 'zod';
import { useSuspenseQuery } from '@apollo/client';
import { QUERY_LOGIN_PAGE } from '@edwin-group/people-graphql';
import useRecaptcha from '@/hooks/useRecaptcha';
import { useCallback, useEffect, useState } from 'react';
import { useCompleteLogin } from './useCompleteLogin';

const LOCKED_STATUS = 429;
// Counting down from the first failure would be noise; the warning only earns
// its place once the lock is close enough to act on.
const WARN_FROM_REMAINING = 5;
// Shown when the server locks the account but the Retry-After header is missing
// (a proxy stripping it, say) - better a vague wait than no explanation at all.
const FALLBACK_LOCK_SECONDS = 15 * 60;

/**
 * The server answers a wrong password with a deliberately generic message and
 * puts the attempt budget in `details`. This is the only warning the user gets
 * before the tenth failure locks them out, so it stands in for the generic copy
 * entirely - hence the self-contained wording.
 */
const lockoutWarning = (error: { details?: { remainingAttempts?: number } }) => {
  const remaining = error?.details?.remainingAttempts;

  if (typeof remaining !== 'number' || remaining > WARN_FROM_REMAINING || remaining < 1) {
    return '';
  }

  return `Incorrect email or password. You have ${remaining} attempt${
    remaining === 1 ? '' : 's'
  } left before your account is temporarily locked.`;
};

const formatCountdown = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) {
    return `${seconds} second${seconds === 1 ? '' : 's'}`;
  }

  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

const formSchema = z.object({
  identifier: z.string().min(1, { message: 'Identifier is required' }),
  password: z.string().min(1, { message: 'Password is required' }),
  rememberMe: z.boolean().optional(),
});

export type FormInputs = z.infer<typeof formSchema>;

export default function useLogin() {
  const { data } = useSuspenseQuery<LoginPageQuery>(QUERY_LOGIN_PAGE, {
    fetchPolicy: 'cache-first',
    context: {
      skipAuth: true,
    },
  });

  const loginPage = data?.loginPage?.data?.attributes;

  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    handleSubmit,
    reset,
    register,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>({
    resolver: zodResolver(
      z.object({
        identifier: z
          .string()
          .min(1, { message: loginPage?.fieldErrors?.identifierRequired || 'Identifier is required' }),
        password: z.string().min(1, { message: loginPage?.fieldErrors?.passwordRequired || 'Password is required' }),
        rememberMe: z.boolean().optional(),
      }),
    ),
    mode: 'onBlur',
  });

  const { enabled: recaptchaEnabled, execute: executeRecaptcha } = useRecaptcha();
  const [formError, setFormError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // The lock is held server-side; this timer only drives the label and the
  // disabled button, so an expired one simply hands control back to the form.
  useEffect(() => {
    if (!lockedUntil) {
      setSecondsLeft(0);
      return;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setSecondsLeft(remaining);

      if (remaining === 0) {
        setLockedUntil(null);
        setFormError(null);
      }
    };

    tick();
    const interval = window.setInterval(tick, 1000);

    return () => window.clearInterval(interval);
  }, [lockedUntil]);

  const isLocked = secondsLeft > 0;
  const location = useLocation();
  const isConfirmation = location.pathname.includes('confirmed');
  const completeLogin = useCompleteLogin();

  const onLogin = async (data: {
    identifier: string;
    password: string;
    rememberMe?: boolean;
    errorMessage:
      | {
          title?: string | null;
          message?: string | null;
        }
      | undefined
      | null;
  }) => {
    setFormError(null);

    const recaptchaToken = await executeRecaptcha('login');

    // The backend rejects a tokenless login once reCAPTCHA is configured, so a
    // blocked Google script would surface as "Invalid credentials" against a
    // correct password. Say what actually went wrong instead.
    if (recaptchaEnabled && !recaptchaToken) {
      const message =
        'We could not complete the security check. Please disable any ad blocker for this site and try again.';
      setFormError(message);
      toast({ title: 'Security check unavailable', description: message, variant: 'destructive' });
      return;
    }

    const result = await login({
      email: data.identifier,
      password: data.password,
      rememberMe: data.rememberMe || false,
      recaptchaToken,
    });

    // A lock answers 429 with a deliberately generic body, so the CMS copy for
    // "wrong password" must not be pasted over it - the wait is the whole message.
    if (result.status === LOCKED_STATUS) {
      const retryAfterSec = result.retryAfterSec ?? FALLBACK_LOCK_SECONDS;
      setLockedUntil(Date.now() + retryAfterSec * 1000);
      toast({
        title: 'Too many sign-in attempts',
        description: `Your account is temporarily locked. Try again in ${formatCountdown(retryAfterSec)}.`,
        variant: 'destructive',
      });
      return;
    }

    if (result.error) {
      // The warning replaces the generic copy rather than trailing it: it already
      // says the sign-in failed, and the impending lock is the part worth reading.
      const message = lockoutWarning(result.error) || data?.errorMessage?.message || result.error.message;
      setFormError(message);
      toast({
        title: data?.errorMessage?.title || 'Error signing in',
        description: message,
        variant: 'destructive',
      });
      return;
    }

    setFormError(null);
    setLockedUntil(null);

    reset();

    // MFA disabled globally (MFA_ENABLED=false): the password step returns a
    // full session directly, so finish signing in here.
    if (result.data?.token && result.data?.user) {
      await completeLogin({ token: result.data.token, user: result.data.user });
      return;
    }

    // MFA enabled: the password step never returns a session. Route to the
    // enrolment or verification screen, carrying the short-lived mfaToken in
    // router state (never in the URL) and preserving any callbackUrl.
    const mfaToken = result.data?.mfaToken;
    if (mfaToken && result.data?.mfaSetupRequired) {
      navigate(`/login/mfa-setup${location.search}`, { state: { mfaToken } });
      return;
    }
    if (mfaToken && result.data?.mfaRequired) {
      navigate(`/login/mfa${location.search}`, { state: { mfaToken } });
      return;
    }

    toast({
      title: data?.errorMessage?.title || 'Error signing in',
      description: data?.errorMessage?.message || 'Unexpected sign-in response. Please try again.',
      variant: 'destructive',
    });
  };

  const onSubmit: SubmitHandler<FormInputs> = useCallback(
    async (data) => {
      if (isLocked) {
        return;
      }

      return await onLogin({
        identifier: data.identifier,
        password: data.password,
        rememberMe: data.rememberMe || false,
        errorMessage: loginPage?.popUpMessages?.error,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLocked, loginPage],
  );

  const lockoutMessage = isLocked
    ? `Too many failed sign-in attempts. Your account is temporarily locked - try again in ${formatCountdown(
        secondsLeft,
      )}.`
    : null;

  return {
    loginPage,
    isConfirmation,
    handleSubmit: handleSubmit(onSubmit),
    register,
    setValue,
    onLogin,
    errors,
    isSubmitting,
    isLocked,
    // Drives the reCAPTCHA disclosure: the badge is hidden via CSS, so the
    // required Google notice is shown in-page only while the check is active.
    recaptchaEnabled,
    // Lockout wins: it is the newest and the more actionable of the two.
    formError: lockoutMessage ?? formError,
  };
}
